import { createZustandContext } from "@/lib/store/zustand-context-pattern";

import type { AddNetworkFormData } from "../-schema/add-network-schema";
import { addNetworkSchema } from "../-schema/add-network-schema";

type ValidationErrors = Record<string, string>;

type AddNetworkFormState = AddNetworkFormData & {
  errors: ValidationErrors;
  mustOnly: boolean;
};

type AddNetworkFormActions = {
  update: <K extends keyof AddNetworkFormData>(
    key: K,
    value: AddNetworkFormData[K],
  ) => void;
  setErrors: (errors: ValidationErrors) => void;
  clearErrors: () => void;
  clearError: (field: string) => void;
  toggleMustOnly: () => void;
  reset: () => void;
  validate: () => boolean;
};

type State = AddNetworkFormState & AddNetworkFormActions;

const initialValues: AddNetworkFormData = {
  networkName: "",
  networkType: undefined,
  connectionType: undefined,
  networkCategory: undefined,
  purpose: "",
  destinationType: undefined,
  placeId: undefined,
  deviceId: undefined,
  adminContact: undefined,
  accessLevel: undefined,
  accessStatus: undefined,
  accessPeriodStartDate: undefined,
  accessPeriodEndDate: undefined,
  subjectType: undefined,
};

export const {
  context: AddNetworkFormStore,
  useStore: useAddNetworkFormStore,
} = createZustandContext<State>(
  (set, get) => ({
    ...initialValues,
    errors: {},
    mustOnly: false,

    update: (key, value) =>
      set((state) => ({
        ...state,
        [key]: value,
      })),

    setErrors: (errors) =>
      set((state) => ({
        ...state,
        errors,
      })),

    clearErrors: () =>
      set((state) => ({
        ...state,
        errors: {},
      })),

    clearError: (field) =>
      set((state) => ({
        ...state,
        errors: {
          ...state.errors,
          [field]: "",
        },
      })),

    toggleMustOnly: () =>
      set((state) => ({
        ...state,
        mustOnly: !state.mustOnly,
      })),

    reset: () =>
      set((state) => ({
        ...state,
        ...initialValues,
        errors: {},
      })),

    validate: () => {
      const state = get();
      const formData: AddNetworkFormData = {
        networkName: state.networkName,
        networkType: state.networkType,
        connectionType: state.connectionType,
        networkCategory: state.networkCategory,
        purpose: state.purpose,
        destinationType: state.destinationType,
        placeId: state.placeId,
        deviceId: state.deviceId,
        adminContact: state.adminContact,
        accessLevel: state.accessLevel,
        accessStatus: state.accessStatus,
        accessPeriodStartDate: state.accessPeriodStartDate,
        accessPeriodEndDate: state.accessPeriodEndDate,
        subjectType: state.subjectType,
      };

      const result = addNetworkSchema.safeParse(formData);
      if (result.success) {
        set((state) => ({
          ...state,
          errors: {},
        }));
        return true;
      }

      const errors: ValidationErrors = {};
      result.error.errors.forEach((error) => {
        const field = error.path.join(".");
        errors[field] = error.message;
      });

      set((state) => ({
        ...state,
        errors,
      }));
      return false;
    },
  }),
  { name: "add-network-form-store" },
);
